import { OidcClient, OidcClientSettings, User } from "oidc-client";
import { AxiosResponse } from "axios";
import { analytics } from "app/helpers/analyticsHelper";
export type AuthenticationErrorHandler = (
  response: AxiosResponse
) => Promise<boolean>;
export interface AdB2CSettings extends OidcClientSettings {
  errorHandlers?: AuthenticationErrorHandler[];
}

export class AdB2CAuth {
  private readonly _client: OidcClient;
  private readonly _storage: Storage;
  private static readonly sessionKey = "AdB2CAuth$$token_v2";

  readonly errorHandlers: Map<any, AuthenticationErrorHandler>;

  constructor(
    settings: AdB2CSettings,
    storage: Storage = window.sessionStorage
  ) {
    this._client = new OidcClient(settings);
    this._storage = storage;
    this.errorHandlers = new Map<any, AuthenticationErrorHandler>(
      settings.errorHandlers?.map((h, i) => [Symbol(`$handler_${i}`), h]) ?? []
    );
  }

  get user(): User | undefined {
    const data = this._storage.getItem(AdB2CAuth.sessionKey);
    return data ? User.fromStorageString(data) : void 0;
  }

  get hasValidToken() {
    return this.user?.expired !== true && this.user?.access_token;
  }

  get token() {
    return this.hasValidToken ? this.user?.access_token : undefined;
  }

  async checkAuthResponse() {
    const hash = document.location.href;
    if (AdB2CAuth._isAuthResponseHash(hash)) {
      const errorMessage = hash.match(/error_description=([^&]+)/);
      if (errorMessage) {
        throw new Error(
          decodeURIComponent(errorMessage[1]).replace(/\+/g, " ")
        );
      }
      try {
        const response = await this._client.processSigninResponse(hash);
        window.history.pushState(
          "",
          document.title,
          `${window.location.pathname}${window.location.search}`
        );

        this._storage.setItem(
          AdB2CAuth.sessionKey,
          new User(response as any).toStorageString()
        );
      } catch (e) {
        throw new Error(`Unexpected authorization response. ${e?.toString()}`);
      }
    }
  }

  async signOut(): Promise<void> {
    window.sessionStorage.removeItem(AdB2CAuth.sessionKey);
    analytics.userLogout();
    window.location.href = (
      await this._client.createSignoutRequest({
        id_token_hint: this.user?.id_token,
        post_logout_redirect_uri: (
          await this._client.createSigninRequest()
        ).url,
      })
    ).url;
  }

  private static _isAuthResponseHash(hash: string) {
    return /(state|id_token)=/.test(hash);
  }
}
